<template>
    <div>
        <!-- Main Group Manager Modal -->
        <themed-modal
            name="attendee-group-manager-modal"
            title="Manage Attendee Groups"
            size="lg"
            @closed="handleMainModalClose"
        >
            <div class="attendee-group-manager">
                <div class="attendee-group-manager__header">
                    <themed-button
                        variant="default"
                        size="sm"
                        @click="showCreateGroupModal = true"
                    >
                        <theme-icon icon="plus" type="icons" />
                        Create Group
                    </themed-button>
                </div>

                <themed-table
                    v-if="groups.length > 0"
                    :columns="tableColumns"
                    :data="groups"
                    :loading="false"
                    :auto-render="true"
                    :pagination="false"
                    class="attendee-group-manager__table"
                >
                    <template #column-members="{ row }">
                        <div v-if="row.members && row.members.length > 0" class="attendee-group-manager__members-cell">
                            <div
                                v-for="member in row.members.slice(0, 3)"
                                :key="member.id"
                                class="attendee-group-manager__member-badge"
                            >
                                <div class="attendee-group-manager__member-avatar">
                                    {{ getInitials(member.display_name || member.attendee_name) }}
                                </div>
                                <span class="attendee-group-manager__member-email">
                                    {{ member.attendee_email }}
                                </span>
                            </div>
                            <span v-if="row.members.length > 3" class="attendee-group-manager__more-count">
                                +{{ row.members.length - 3 }} more
                            </span>
                        </div>
                        <span v-else class="attendee-group-manager__no-members">No members</span>
                    </template>

                    <template #column-actions="{ row }">
                        <div class="attendee-group-manager__actions">
                            <themed-button
                                variant="ghost"
                                size="sm"
                                @click="editGroup(row)"
                                title="Edit group"
                            >
                                <theme-icon icon="edit" type="icons" />
                            </themed-button>
                            <themed-button
                                v-if="canDeleteGroup"
                                variant="ghost"
                                size="sm"
                                @click="deleteGroup(row)"
                                title="Delete group"
                            >
                                <theme-icon icon="trash" type="icons" />
                            </themed-button>
                        </div>
                    </template>
                </themed-table>

                <div v-else class="attendee-group-manager__empty">
                    <theme-icon icon="users" type="icons" />
                    <p>No groups yet. Create a group to organize attendees.</p>
                </div>
            </div>
        </themed-modal>

        <!-- Create/Edit Group Modal -->
        <themed-modal
            v-if="showCreateGroupModal || editingGroup"
            name="group-form-modal"
            :title="editingGroup ? 'Edit Group' : 'Create Group'"
            size="md"
            @closed="closeGroupModal"
        >
            <themed-form
                :fields="groupFormFields"
                :model-value="groupForm"
                layout="vertical"
                @input="groupForm = $event"
            />

            <template #footer>
                <div class="attendee-group-manager__modal-footer">
                    <themed-button
                        variant="secondary"
                        @click="closeGroupModal"
                    >
                        Cancel
                    </themed-button>
                    <themed-button
                        variant="default"
                        @click="saveGroup"
                        :disabled="!groupForm.name || savingGroup"
                    >
                        {{ savingGroup ? 'Saving...' : (editingGroup ? 'Update' : 'Create') }}
                    </themed-button>
                </div>
            </template>
        </themed-modal>
    </div>
</template>

<script>
import axios from 'axios';
import ThemedButton from '../ThemedButton.vue';
import ThemedModal from '../ThemedModal.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedForm from '../form/ThemedForm.vue';

export default {
    name: 'AttendeeGroupManager',

    components: {
        ThemedButton,
        ThemedModal,
        ThemedTable,
        ThemeIcon,
        ThemedForm,
    },

    props: {
        tourId: {
            type: Number,
            required: true,
        },
        availableUsers: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            groups: [],
            showCreateGroupModal: false,
            editingGroup: null,
            groupForm: {
                name: '',
                description: '',
                selectedMembers: [],
                externalEmails: '',
            },
            savingGroup: false,
            tableColumns: [
                {
                    label: 'Group Name',
                    field: 'name',
                    sortable: true,
                },
                {
                    label: 'Description',
                    field: 'description',
                    sortable: false,
                    formatter: (value) => value || '-',
                },
                {
                    label: 'Members',
                    field: 'members',
                    sortable: false,
                    width: '200px',
                    customSlot: true,
                },
                {
                    label: 'Actions',
                    field: 'actions',
                    sortable: false,
                    width: '100px',
                    align: 'right',
                    customSlot: true,
                },
            ],
        };
    },

    computed: {
        availableUserOptions() {
            return this.availableUsers.map(user => ({
                label: user.email ? `${user.name} (${user.email})` : user.name,
                value: user.id,
                ...user
            }));
        },

        canDeleteGroup() {
            return this.$gate && (this.$gate.isAdmin() || this.$gate.isClientAdmin());
        },

        groupFormFields() {
            const fields = [
                {
                    name: 'name',
                    type: 'input',
                    label: 'Group Name',
                    required: true,
                    placeholder: 'Enter group name',
                    validation: {
                        required: true
                    }
                },
                {
                    name: 'description',
                    type: 'textarea',
                    label: 'Description',
                    placeholder: 'Enter group description',
                    rows: 3,
                    helpText: 'Optional'
                }
            ];

            // Only show member selection when creating new group
            if (!this.editingGroup) {
                fields.push({
                    name: 'selectedMembers',
                    type: 'multiselect',
                    label: 'Add Members',
                    placeholder: 'Search users by name or email',
                    helpText: 'You can add members now or later',
                    componentProps: {
                        options: this.availableUserOptions,
                        searchable: true,
                        closeOnSelect: false,
                        trackBy: 'value',
                        label: 'label'
                    }
                });
            }

            fields.push({
                name: 'externalEmails',
                type: 'textarea',
                label: 'Add External Members by Email',
                placeholder: 'email1@example.com, email2@example.com',
                rows: 3,
                helpText: 'Enter email addresses separated by commas'
            });

            return fields;
        }
    },

    mounted() {
        this.loadGroups();
        // Use setTimeout to ensure modal is registered before showing
        setTimeout(() => {
            this.$modal.show('attendee-group-manager-modal');
        }, 100);
    },

    beforeDestroy() {
        // Clean up modal when component is destroyed
        if (this.$modal) {
            this.$modal.hide('attendee-group-manager-modal');
        }
    },

    watch: {
        showCreateGroupModal(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.$modal.show('group-form-modal');
                });
            }
        },
        editingGroup(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.$modal.show('group-form-modal');
                });
            }
        }
    },

    methods: {
        async loadGroups() {
            try {
                const response = await axios.get('/tour-groups');
                this.groups = response.data;
            } catch (error) {
                console.error('Failed to load groups:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load groups');
                }
            }
        },

        editGroup(group) {
            this.editingGroup = group;
            
            // Extract external member emails (members without user_id)
            const externalEmails = group.members
                ? group.members
                    .filter(member => !member.user_id)
                    .map(member => member.attendee_email)
                    .join(', ')
                : '';
            
            this.groupForm = {
                name: group.name,
                description: group.description || '',
                selectedMembers: [],
                externalEmails: externalEmails,
            };
            this.$nextTick(() => {
                this.$modal.show('group-form-modal');
            });
        },

        async deleteGroup(group) {
            const result = await this.$swal({
                title: `Delete group "${group.name}"?`,
                text: 'This will not remove attendees from tours.',
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary',
                cancelButtonText: 'Cancel',
            });

            if (!result.value) {
                return;
            }

            try {
                await axios.delete(`/tour-groups/${group.id}`);
                await this.loadGroups();
                if (this.$toast) {
                    this.$toast.success('Group deleted successfully');
                }
            } catch (error) {
                console.error('Failed to delete group:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to delete group');
                }
            }
        },

        async saveGroup() {
            this.savingGroup = true;
            try {
                const payload = {
                    name: this.groupForm.name,
                    description: this.groupForm.description,
                };

                // Prepare members array
                const members = [];

                // Add selected users
                if (!this.editingGroup && this.groupForm.selectedMembers.length > 0) {
                    this.groupForm.selectedMembers.forEach(member => {
                        members.push({
                            user_id: member.value || member.id,
                        });
                    });
                }

                // Add external emails
                if (this.groupForm.externalEmails.trim()) {
                    const emails = this.groupForm.externalEmails
                        .split(',')
                        .map(e => e.trim())
                        .filter(e => e.length > 0);
                    
                    emails.forEach(email => {
                        members.push({
                            attendee_email: email,
                            attendee_name: email.split('@')[0],
                        });
                    });
                }

                if (members.length > 0) {
                    payload.members = members;
                }

                if (this.editingGroup) {
                    await axios.put(`/tour-groups/${this.editingGroup.id}`, payload);
                    
                    // If editing and there are new members to add
                    if (members.length > 0) {
                        await axios.post(`/tour-groups/${this.editingGroup.id}/members`, {
                            members: members
                        });
                    }
                } else {
                    await axios.post('/tour-groups', payload);
                }

                await this.loadGroups();
                this.closeGroupModal();
                
                if (this.$toast) {
                    this.$toast.success(`Group ${this.editingGroup ? 'updated' : 'created'} successfully`);
                }
                
                this.$emit('groups-updated');
            } catch (error) {
                console.error('Failed to save group:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to save group');
                }
            } finally {
                this.savingGroup = false;
            }
        },

        closeGroupModal() {
            this.showCreateGroupModal = false;
            this.editingGroup = null;
            this.groupForm = {
                name: '',
                description: '',
                selectedMembers: [],
                externalEmails: '',
            };
            this.$modal.hide('group-form-modal');
        },

        handleMainModalClose() {
            this.$emit('close');
            this.$modal.hide('attendee-group-manager-modal');
        },

        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
    },
};
</script>
